/**
 * Parsing helper for agent references pasted into the app. Shared by the web
 * `@`-mention run picker (which lets you reference a run by pasting its id or
 * URL) so a bare agent id or a link to an agent page resolves to that run even
 * though neither substring-matches the run's name or prompt.
 */

import { coreIds } from './ids.js';

// An agent page URL ends in `/agents/<id>` — both the canonical
// `/orgs/<slug>/agents/<id>` and the legacy `/agents/<id>` shape (also used in
// server-issued links like Slack notifications). We capture each `/agents/`
// segment loosely and validate it with `coreIds.isOf`, keeping `ids.ts` the one
// source of truth for the id shape. The `+` stops at the first non-id character
// (a `/`, `?`, `#`, whitespace, closing paren, or the end), so a query string,
// fragment, or trailing text pasted alongside the URL rides along harmlessly.
// The `g` flag lets us skip a non-id segment (e.g. `/agents/list?next=…`) and
// keep scanning for a real id further along.
const AGENT_PATH_RE = /\/agents\/([A-Za-z0-9_]+)/g;

/**
 * Extract an agent id from free-text input, or `null` when there is none.
 * Accepts a bare agent id (`agt_…`) or any string containing an agent page
 * path/URL (`https://app.example.com/orgs/acme/agents/agt_…?tab=diff`,
 * `/agents/agt_…`). Returns the first agent id found.
 */
export function extractAgentIdFromInput(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  // A bare id pasted directly.
  if (coreIds.isOf('agent', trimmed)) return trimmed;
  // An agent page path or URL — first `/agents/<segment>` that is a valid id.
  for (const match of trimmed.matchAll(AGENT_PATH_RE)) {
    if (coreIds.isOf('agent', match[1])) return match[1];
  }
  return null;
}
